import { useState } from 'react';
import { api } from '@/api/client';
import { InterviewPrepResponse } from '@/api/types';
import { handleAppError } from '@/lib/errorUtils';
import { AIGenerationHookParams } from './ai/types';

export function useInterviewPrep({
  workspaceId,
  selectedId,
  jobText,
  targetDetails,
  setStatus,
  setStatusMessage,
  recordUsage,
  setRightView,
  getSignal,
  setIsGenerating,
  scrollToResults,
}: AIGenerationHookParams) {
  const [interviewPrepResult, setInterviewPrepResult] = useState<InterviewPrepResponse | null>(null);

  const handleGenerateInterviewPrep = async (skipStateUpdates: boolean | any = false) => {
    const isSkip = skipStateUpdates === true;
    if (!selectedId || jobText.trim().length < 50) {
      if (!isSkip) {
        setStatus('error');
        setStatusMessage('Please enter a longer, meaningful job description.');
      }
      return;
    }
    
    setIsGenerating(prev => ({ ...prev, interviewPrep: true }));
    if (!isSkip) {
      setStatus('idle');
      setRightView('interviewPrep');
      scrollToResults();
    }

    try {
      if (!isSkip) await new Promise(r => setTimeout(r, 300));
      const signal = isSkip ? getSignal('package') : getSignal('interviewPrep');
      const result = await api.generateInterviewPrep(workspaceId, { resumeId: selectedId, jobText, ...targetDetails }, { signal });
      if (result.usage) {
        recordUsage(result.usage.cost, result.usage.promptTokens, result.usage.completionTokens);
      }
      
      setInterviewPrepResult(result);
      if (!isSkip) setStatusMessage('Prep guide ready');
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (!isSkip) {
        setStatus('error');
        setStatusMessage(handleAppError(err, 'Generation failed'));
      }
    } finally {
      setIsGenerating(prev => ({ ...prev, interviewPrep: false }));
    }
  };

  return { interviewPrepResult, setInterviewPrepResult, handleGenerateInterviewPrep };
}
